import { captureException } from '@/utils/sentry';
import { DataProxy } from '@apollo/client';
import {
  FILTER_STATUS_SETS,
  INBOUND_LIST_FILTERS,
  InboundListFilter,
} from '@/screens/order-inbounds/order-inbounds-order-list/types';
import {
  getEffectiveInboundQueryFilters,
  getInboundHistoryFilteredVariables,
  hasNonDefaultInboundFilters,
  historyToListShape,
  INBOUND_HISTORY_PAGE_SIZE,
  INBOUND_HISTORY_VARIABLES,
  ORDER_INBOUNDS_LIST_HISTORY_QUERY,
  ORDER_INBOUNDS_LIST_LIVE_UPDATE_QUERY,
  ORDER_INBOUNDS_LIST_QUERY,
  ORDER_INBOUNDS_LIST_SUBSCRIPTION,
  useIsInboundHistoryEnabled,
  useOrderInboundListQueryVariables,
  useUpdateInboundListQueryCache,
  writeHistoryCacheForVariables,
} from '@/screens/order-inbounds/queries';
import {
  InboundListQueryFilters,
  useOrderInboundsStore,
} from '@/stores/order-inbounds-store';
import { OrderInboundListFieldsFragment } from '@/screens/order-inbounds/types';
import {
  OrderInboundListSubscriptionFieldsFragment,
  OrderInbounds_ListHistoryQuery,
  OrderInbounds_ListHistoryQueryVariables,
  OrderInbounds_ListLiveUpdateQuery,
  OrderInbounds_ListLiveUpdateQueryVariables,
  OrderInbounds_ListQuery,
  OrderInbounds_ListQueryVariables,
  OrderInbounds_ListSubscriptionSubscription,
  OrderInbounds_ListSubscriptionSubscriptionVariables,
} from '@/types';
import { partnerSupplier } from '@/utils/environment';
import { PROCESSING_STATUSES } from '@/screens/order-inbounds/utils';
import { SmartOrderProcessingStatus, SmartOrderStatus } from '@/enums';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import { useLazyQuery } from '@/hooks/use-lazy-query';
import { useSubscription } from '@/contexts/subscription-context';
import { useToasts } from '@/contexts/toast-context';
import { useWrappedQuery } from '@/hooks/use-wrapped-query';

const areSetsEqual = (set1: Set<string>, set2: Set<string>) => {
  if (set1.size !== set2.size) {
    return false;
  }
  return Array.from(set1).every((value) => set2.has(value));
};

// What a subscription push means for an order the cache already holds:
//
// - `none`     the pushed fields are enough; patch them in place.
// - `refresh`  the order's own data is stale in a way the subscription can't
//              express (not all fields the list renders are in it), so refetch
//              it -- but it is still the same order in the same place.
// - `relocate` its status just moved it into a tab whose cache has never
//              fetched it. Each tab's cache is its own status-filtered query,
//              and the live patch below can only update or remove rows an
//              entry already holds, so a refetch is the only way to get the
//              row into its new tab. Only possible in the history-enabled
//              multi-tab world; the legacy single query isn't status-scoped,
//              so a plain field patch already keeps status current there.
//
// The distinction between `refresh` and `relocate` is what keeps a refetched
// order from being mistaken for a new arrival -- see
// buildBackfillCacheUpdate.
export type ExistingOrderBackfillAction = 'none' | 'refresh' | 'relocate';

export const getExistingOrderBackfillAction = (
  existingOrder: OrderInboundListFieldsFragment,
  updatedFields: OrderInboundListSubscriptionFieldsFragment,
  isInboundHistoryEnabled: boolean,
): ExistingOrderBackfillAction => {
  const hasStatusChangedIntoNewTab =
    isInboundHistoryEnabled &&
    Boolean(updatedFields.status) &&
    updatedFields.status !== existingOrder.status;

  if (hasStatusChangedIntoNewTab) {
    return 'relocate';
  }

  const isFinishedReprocessing =
    !PROCESSING_STATUSES.has(
      updatedFields.processing_status as SmartOrderProcessingStatus,
    ) &&
    PROCESSING_STATUSES.has(
      existingOrder.processing_status as SmartOrderProcessingStatus,
    );

  const hadNoItems = !existingOrder.search_inputs.length;

  return isFinishedReprocessing || hadNoItems ? 'refresh' : 'none';
};

interface BackfillCacheUpdateParams {
  // Whether this cache entry is one previously-unseen orders may be added to.
  // Base entries yes (they're always default-sorted, newest first); filtered
  // entries no -- whether an order matches a search term or assignee filter
  // isn't something we can evaluate locally.
  canInsertNewRows: boolean;
  // Full list rows for every order the backfill just fetched.
  fetchedOrders: OrderInboundListFieldsFragment[];
  // Of those, the ones that may be inserted into an entry that doesn't already
  // hold them: orders the cache had never seen, and orders whose status just
  // relocated them into another tab.
  //
  // This is the half that matters. A backfill is not always a new order -- an
  // order the list already holds somewhere gets refetched just to refresh its
  // own data (see `refresh` above), and inserting one of those into an entry
  // that doesn't hold it is wrong: it isn't new, so the top of a newest-first
  // list is the wrong place for it. That is how an old order dragged into the
  // cache by an oldest-first filtered query would end up pinned above
  // genuinely recent ones, and stay there once the sort was set back.
  insertableOrderUUIDs: Set<string>;
  // Whether a status belongs in the tab this entry represents.
  isIncluded: (status: string) => boolean;
}

export const buildBackfillCacheUpdate =
  ({
    canInsertNewRows,
    fetchedOrders,
    insertableOrderUUIDs,
    isIncluded,
  }: BackfillCacheUpdateParams) =>
  (cachedData: OrderInbounds_ListQuery): OrderInbounds_ListQuery => {
    const fetchedOrdersByUUID = fetchedOrders.reduce<
      Record<string, OrderInboundListFieldsFragment>
    >((acc, order) => {
      acc[order.uuid] = order;
      return acc;
    }, {});

    const existingOrderUUIDs = new Set<string>();
    const existingOrders = cachedData.smart_order.reduce<
      OrderInboundListFieldsFragment[]
    >((acc, order) => {
      const updatedOrder = fetchedOrdersByUUID[order.uuid];
      existingOrderUUIDs.add(order.uuid);
      if (updatedOrder) {
        if (isIncluded(updatedOrder.status)) {
          acc.push(updatedOrder);
        }
      } else {
        acc.push(order);
      }
      return acc;
    }, []);

    const newListItems = canInsertNewRows
      ? fetchedOrders.filter(
          (order) =>
            !existingOrderUUIDs.has(order.uuid) &&
            insertableOrderUUIDs.has(order.uuid) &&
            isIncluded(order.status),
        )
      : [];

    // New or newly-relocated orders always land at the top, regardless of
    // their own created_at -- only sort the new batch among themselves by
    // recency, then place them above whatever's already loaded (including
    // paginated pages further down). Already-visible rows never get
    // repositioned.
    const sortedNewListItems = [...newListItems].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
    );

    return {
      ...cachedData,
      smart_order: [...sortedNewListItems, ...existingOrders],
    };
  };

type SmartOrderUpdateMapping = Record<
  string,
  OrderInboundListSubscriptionFieldsFragment
>;

// Which of a tab's two queries is being read: its unfiltered base query, or
// the query for the active filter set (search term, assignees, sort).
type ListMode = 'base' | 'filtered';

const INITIAL_PAGES_FETCHED: Record<InboundListFilter, number> = {
  [InboundListFilter.All]: 1,
  [InboundListFilter.Open]: 1,
  [InboundListFilter.Closed]: 1,
};

interface UseOrderInboundListDataOptions {
  // Order UUIDs that must keep receiving live updates even when they aren't in
  // any currently cached list -- in practice, whatever the detail pane is
  // showing. Without this, an order the user is looking at could be dropped
  // from the subscription's where-clause (e.g. it only ever entered the list
  // via a search that has since been narrowed), and would silently stop
  // updating while still on screen.
  pinnedOrderUUIDs?: string[];
}

// Poll interval is 1 second. Gives enough time to capture multiple new orders.
const GET_NEW_ORDERS_DEBOUNCE_DELAY_MS = 1250;

// Value identity for a filter set, used to tell a fetchMore that resolved
// against the filters it was issued for from one the user has since changed.
// Compared by value rather than by reference because the filter panel hands
// the store a fresh `assignedEmployeeIds` array on every change, so two
// identical filter sets don't necessarily share a reference.
const getFiltersIdentity = (filters: InboundListQueryFilters) =>
  JSON.stringify([
    filters.searchTerm,
    filters.sortBy,
    filters.includeUnassigned,
    [...filters.assignedEmployeeIds].sort(),
  ]);

// Each tab runs two separate queries instead of one query whose variables gain
// the user's filters.
//
// `base` only depends on status, so typing a search, picking assignees or
// changing the sort never affects it. It stays mounted and live for the whole
// session. `filtered` only mounts while at least one non-default filter is
// active. Clearing the filters just swaps back to `base`, which is already
// warm, instead of refetching. This also keeps the subscription's anchor data
// (see the UUID union below) available even when a filter set matches nothing.
//
// The filtered query uses `cache-and-network` so that re-applying a filter set
// always refreshes from the server, instead of trusting a stale cached result.
// The base queries use the default fetch policy.
const useHistoryListPair = (
  filter: InboundListFilter,
  queryFilters: InboundListQueryFilters,
  isEnabled: boolean,
) => {
  // Effective (clamp-aware) filters: a filter set whose only non-default value
  // is a sort this tab clamps away leaves nothing to filter by, so the tab
  // keeps reading its base query rather than issuing a second query whose
  // variables would collide with the base entry's cache key.
  const effectiveFilters = getEffectiveInboundQueryFilters(
    filter,
    queryFilters,
  );
  const hasFilters = hasNonDefaultInboundFilters(effectiveFilters);
  const mode: ListMode = hasFilters ? 'filtered' : 'base';

  const base = useWrappedQuery<
    OrderInbounds_ListHistoryQuery,
    OrderInbounds_ListHistoryQueryVariables
  >(ORDER_INBOUNDS_LIST_HISTORY_QUERY, {
    context: {
      noBatch: true,
    },
    skip: !isEnabled,
    variables: INBOUND_HISTORY_VARIABLES[filter],
  });

  const filtered = useWrappedQuery<
    OrderInbounds_ListHistoryQuery,
    OrderInbounds_ListHistoryQueryVariables
  >(ORDER_INBOUNDS_LIST_HISTORY_QUERY, {
    context: {
      noBatch: true,
    },
    fetchPolicy: 'cache-and-network',
    skip: !isEnabled || !hasFilters,
    // Variables still have to type-check while skipped; with every effective
    // filter at its default there's nothing to filter by, so fall back to the
    // base variables (unused -- the query is skipped -- and never a distinct
    // cache key).
    variables: hasFilters
      ? getInboundHistoryFilteredVariables(filter, effectiveFilters)
      : INBOUND_HISTORY_VARIABLES[filter],
  });

  return { active: hasFilters ? filtered : base, base, filtered, mode };
};

export const useOrderInboundListData = ({
  pinnedOrderUUIDs = [],
}: UseOrderInboundListDataOptions = {}) => {
  const { user } = useAuthenticatedUserContext();
  const isInboundHistoryEnabled = useIsInboundHistoryEnabled();
  const legacyQueryVariables = useOrderInboundListQueryVariables();
  const activeStatusFilter = useOrderInboundsStore(
    (store) => store.activeStatusFilter,
  );
  const queryFilters = useOrderInboundsStore((store) => store.queryFilters);
  const setInboundListCount = useOrderInboundsStore(
    (store) => store.actions.setInboundListCount,
  );
  const { showErrorToast } = useToasts();

  const pendingOrderUUIDs = useRef<Set<string>>(new Set());
  // The subset of pendingOrderUUIDs that may be inserted into a cache entry
  // that doesn't already hold them -- see buildBackfillCacheUpdate. Always a
  // subset: every UUID added here is added to pendingOrderUUIDs too, and both
  // are cleared together when a batch is flushed.
  const pendingInsertableOrderUUIDs = useRef<Set<string>>(new Set());

  // Tracks how many pages have been fetched from the server for each tab --
  // not derived from the cache array's length, since live updates add/remove
  // rows in that same cache entry independently of pagination and would drift
  // the "next page" window (causing duplicate/skipped rows).
  //
  // Base and filtered results are separate cache entries paginated
  // independently, so each gets its own counter. The base counters survive
  // filtering entirely (their query never re-keys, so their pages are still
  // there when the filters clear); only the filtered counters reset, when the
  // filter set changes.
  const pagesFetchedRef = useRef<
    Record<ListMode, Record<InboundListFilter, number>>
  >({
    base: { ...INITIAL_PAGES_FETCHED },
    filtered: { ...INITIAL_PAGES_FETCHED },
  });

  // Guards against duplicate/concurrent fetchMore calls per tab. A ref (not
  // just the mirrored state below) so the check is synchronous and immune to
  // React's state-update batching -- fetchNextPage can be re-invoked (e.g. by
  // FlashList's onEndReached, which never awaits it) before a re-render would
  // otherwise have flushed a state update.
  const isFetchingNextPageRef = useRef<Record<InboundListFilter, boolean>>({
    [InboundListFilter.All]: false,
    [InboundListFilter.Open]: false,
    [InboundListFilter.Closed]: false,
  });
  const [isFetchingNextPageState, setIsFetchingNextPageState] = useState<
    Record<InboundListFilter, boolean>
  >({
    [InboundListFilter.All]: false,
    [InboundListFilter.Open]: false,
    [InboundListFilter.Closed]: false,
  });

  const legacyListData = useWrappedQuery<
    OrderInbounds_ListQuery,
    OrderInbounds_ListQueryVariables
  >(ORDER_INBOUNDS_LIST_QUERY, {
    context: {
      noBatch: true,
    },
    skip: isInboundHistoryEnabled,
    variables: legacyQueryVariables,
  });

  const openLists = useHistoryListPair(
    InboundListFilter.Open,
    queryFilters,
    isInboundHistoryEnabled,
  );
  const closedLists = useHistoryListPair(
    InboundListFilter.Closed,
    queryFilters,
    isInboundHistoryEnabled,
  );
  const allLists = useHistoryListPair(
    InboundListFilter.All,
    queryFilters,
    isInboundHistoryEnabled,
  );

  // The query each tab is currently displaying -- its filtered query while any
  // non-default filter is active, its base query otherwise.
  const openListData = openLists.active;
  const closedListData = closedLists.active;
  const allListData = allLists.active;

  // openListData/closedListData/allListData are new wrapper objects every
  // render (Apollo 3.7 doesn't memoize useQuery's result), so grouping them
  // into an object bought nothing -- the group was just as unstable as its
  // inputs. Select directly instead of allocating a lookup object.
  const activeHistoryListData =
    activeStatusFilter === InboundListFilter.Open
      ? openListData
      : activeStatusFilter === InboundListFilter.Closed
        ? closedListData
        : allListData;

  // activeHistoryListData/legacyListData are new wrapper objects every render
  // (Apollo 3.7 doesn't memoize useQuery's result), and historyToListShape
  // allocates a new array/object on every call -- so without this memo,
  // baseListData.data (and the hook's returned `data`) would be a new
  // reference every render even though nothing changed. Picking the specific
  // fields we use (rather than spreading the whole query result) keeps the
  // dependency list below genuinely exhaustive, so no eslint-disable is
  // needed. If a consumer starts reading another field off baseListData (e.g.
  // networkStatus), add it to both branches below and to this dependency list.
  const baseListData = useMemo(() => {
    if (!isInboundHistoryEnabled) {
      return {
        client: legacyListData.client,
        data: legacyListData.data,
        error: legacyListData.error,
        hasResponse: legacyListData.hasResponse,
        loading: legacyListData.loading,
      };
    }
    return {
      client: activeHistoryListData.client,
      data: historyToListShape(activeHistoryListData.data),
      error: activeHistoryListData.error,
      hasResponse: activeHistoryListData.hasResponse,
      loading: activeHistoryListData.loading,
    };
  }, [
    isInboundHistoryEnabled,
    activeHistoryListData.client,
    activeHistoryListData.data,
    activeHistoryListData.error,
    activeHistoryListData.hasResponse,
    activeHistoryListData.loading,
    legacyListData.client,
    legacyListData.data,
    legacyListData.error,
    legacyListData.hasResponse,
    legacyListData.loading,
  ]);

  // Sync the store's counts from each query's authoritative total_count.
  // total_count is kept live-accurate by every cache write for this filter --
  // both real fetches (refetch/fetchMore, returning the server's fresh
  // total_count) and cache-only patches (mutations, live-subscription
  // updates, and the debounced backfill), which fold the same net
  // list-length delta into the previously cached total_count
  // (mergeListShapeIntoHistoryQuery in queries.ts). Because this effect does
  // an absolute set on every such change, any earlier optimistic nudge to
  // inboundListCounts (e.g. submitQueueSmartOrders) is always overwritten by
  // the next authoritative value here -- no permanent drift is possible.
  useEffect(() => {
    const totalCount =
      openListData.data?.getSmartOrderInboundHistory?.total_count;
    if (totalCount !== undefined) {
      setInboundListCount(InboundListFilter.Open, totalCount);
    }
  }, [
    openListData.data?.getSmartOrderInboundHistory?.total_count,
    setInboundListCount,
  ]);

  useEffect(() => {
    const totalCount =
      closedListData.data?.getSmartOrderInboundHistory?.total_count;
    if (totalCount !== undefined) {
      setInboundListCount(InboundListFilter.Closed, totalCount);
    }
  }, [
    closedListData.data?.getSmartOrderInboundHistory?.total_count,
    setInboundListCount,
  ]);

  // Manual refresh should re-sync every tab's data/counts, not just whichever
  // one is currently displayed -- otherwise refreshing while on Open wouldn't
  // update the Closed count shown right next to it. Refreshes the base
  // queries always, plus the filtered queries when any filter is on, so both
  // the visible rows and the list underneath them come back current.
  const refetchAll = useCallback(async () => {
    if (isInboundHistoryEnabled) {
      pagesFetchedRef.current = {
        base: { ...INITIAL_PAGES_FETCHED },
        filtered: { ...INITIAL_PAGES_FETCHED },
      };
      const results = await Promise.allSettled(
        [openLists, closedLists, allLists].flatMap((lists) => [
          lists.base.refetch(),
          ...(lists.mode === 'filtered' ? [lists.filtered.refetch()] : []),
        ]),
      );
      if (results.some((result) => result.status === 'rejected')) {
        showErrorToast();
      }
      return;
    }
    await legacyListData.refetch();
  }, [
    isInboundHistoryEnabled,
    openLists,
    closedLists,
    allLists,
    legacyListData,
    showErrorToast,
  ]);

  const [isRetryingLoad, setIsRetryingLoad] = useState(false);
  const isRetryingLoadRef = useRef(false);

  const retryActiveTab = useCallback(async () => {
    if (isRetryingLoadRef.current) {
      return;
    }
    isRetryingLoadRef.current = true;
    setIsRetryingLoad(true);
    try {
      if (isInboundHistoryEnabled) {
        const filter = activeStatusFilter;
        const lists =
          filter === InboundListFilter.Open
            ? openLists
            : filter === InboundListFilter.Closed
              ? closedLists
              : allLists;
        await lists.active.refetch();
        pagesFetchedRef.current[lists.mode][filter] = 1;
      } else {
        await legacyListData.refetch();
      }
    } catch {
    } finally {
      isRetryingLoadRef.current = false;
      setIsRetryingLoad(false);
    }
  }, [
    activeStatusFilter,
    isInboundHistoryEnabled,
    openLists,
    closedLists,
    allLists,
    legacyListData,
  ]);

  const hasLoadFailed =
    Boolean(baseListData.error) && !baseListData.data && !isRetryingLoad;

  // Each new filter set is its own query starting at offset 0, so the filtered
  // page counters restart with it. The base counters are deliberately
  // untouched -- their queries aren't re-keyed by filtering, so the pages
  // already fetched there are still valid when the filters clear.
  useEffect(() => {
    if (isInboundHistoryEnabled) {
      pagesFetchedRef.current.filtered = { ...INITIAL_PAGES_FETCHED };
    }
  }, [isInboundHistoryEnabled, queryFilters]);

  const isFetchingNextPage = isInboundHistoryEnabled
    ? Boolean(isFetchingNextPageState[activeStatusFilter])
    : false;

  // Advances whichever tab is currently active. All 3 history queries stay
  // permanently mounted regardless of activeStatusFilter, so a fetchMore
  // in flight for a tab the user has since navigated away from is never
  // cancelled -- it resolves and writes into that tab's cache entry
  // whichever tab is being viewed at the time.
  const fetchNextPage = useCallback(async () => {
    if (!isInboundHistoryEnabled) {
      return;
    }

    const filter = activeStatusFilter;
    const lists =
      filter === InboundListFilter.Open
        ? openLists
        : filter === InboundListFilter.Closed
          ? closedLists
          : allLists;
    // Page whichever of the tab's two queries is on screen, against its own
    // page counter -- the base and filtered result sets are independent.
    const { mode } = lists;
    const listData = lists[mode];
    const totalCount =
      listData.data?.getSmartOrderInboundHistory?.total_count ?? 0;
    const nextOffset =
      pagesFetchedRef.current[mode][filter] * INBOUND_HISTORY_PAGE_SIZE;

    if (isFetchingNextPageRef.current[filter] || nextOffset >= totalCount) {
      return;
    }

    isFetchingNextPageRef.current[filter] = true;
    setIsFetchingNextPageState((prev) => ({ ...prev, [filter]: true }));

    // Grab the filter set now -- by the time this fetchMore resolves the user
    // could've typed something else, picked different assignees or changed the
    // sort, and we don't want to touch that page counter or cache entry,
    // that's not what we asked for. Only the filtered entries are at risk:
    // the base query isn't keyed by filters, so a base page stays valid no
    // matter what the user changes while it's in flight.
    const requestedFiltersIdentity =
      mode === 'filtered' ? getFiltersIdentity(queryFilters) : null;
    const isStillCurrent = () =>
      requestedFiltersIdentity === null ||
      getFiltersIdentity(useOrderInboundsStore.getState().queryFilters) ===
        requestedFiltersIdentity;

    try {
      await listData.fetchMore({
        updateQuery: (previousResult, { fetchMoreResult }) => {
          if (!fetchMoreResult?.getSmartOrderInboundHistory) {
            return previousResult;
          }
          if (!isStillCurrent()) {
            return previousResult;
          }

          const existingUUIDs = new Set(
            previousResult.getSmartOrderInboundHistory.smart_orders
              .map((item) => item.smart_order?.uuid)
              .filter(Boolean),
          );
          // Guards against pagination drift: a live update could have
          // already inserted an order into this cache entry between the
          // last page fetch and this one resolving, and naive concatenation
          // could re-show it if the server's next page window happens to
          // include it again.
          const newItems =
            fetchMoreResult.getSmartOrderInboundHistory.smart_orders.filter(
              (item) =>
                item.smart_order && !existingUUIDs.has(item.smart_order.uuid),
            );

          return {
            getSmartOrderInboundHistory: {
              ...fetchMoreResult.getSmartOrderInboundHistory,
              smart_orders: [
                ...previousResult.getSmartOrderInboundHistory.smart_orders,
                ...newItems,
              ],
            },
          };
        },
        variables: {
          ...(mode === 'filtered'
            ? getInboundHistoryFilteredVariables(filter, queryFilters)
            : INBOUND_HISTORY_VARIABLES[filter]),
          offset: nextOffset,
        },
      });
      if (isStillCurrent()) {
        pagesFetchedRef.current[mode][filter] += 1;
      }
    } catch {
      showErrorToast();
    } finally {
      isFetchingNextPageRef.current[filter] = false;
      setIsFetchingNextPageState((prev) => ({ ...prev, [filter]: false }));
    }
  }, [
    isInboundHistoryEnabled,
    activeStatusFilter,
    queryFilters,
    openLists,
    closedLists,
    allLists,
    showErrorToast,
  ]);

  const updateInboundListQueryCache = useUpdateInboundListQueryCache();

  // Applies `buildUpdateFn` to whichever cache(s) are relevant: when history
  // is enabled, once per filter (Open/Closed/All), each with its own
  // "does this status belong here" predicate; when it's not, once against the
  // single legacy cache (predicate always true, since the legacy query's own
  // where-clause already handles status scoping).
  //
  // `canInsertNewRows` tells the update function whether the entry it's about
  // to write is one we're allowed to add previously-unseen orders to. Status
  // is something we can evaluate locally; whether an order matches a search
  // term or assignee filter is not -- and under a non-default sort the top of
  // the list is the wrong place for it even when it does match -- so filtered
  // entries only ever get updates to rows they already hold. Base entries
  // always accept inserts (they're always default-sorted, newest first) --
  // that's what makes a new order that arrived while filtering already be
  // there when the filters clear. Which of the fetched orders actually count
  // as insertable is a separate question, decided per order rather than per
  // entry (buildBackfillCacheUpdate).
  const applyLiveCacheUpdate = useCallback(
    (
      client: DataProxy,
      buildUpdateFn: (
        isIncluded: (status: string) => boolean,
        canInsertNewRows: boolean,
      ) => (cachedData: OrderInbounds_ListQuery) => OrderInbounds_ListQuery,
    ) => {
      if (!isInboundHistoryEnabled) {
        updateInboundListQueryCache(
          client,
          buildUpdateFn(() => true, true),
        );
        return;
      }
      for (const filter of INBOUND_LIST_FILTERS) {
        const isIncluded = (status: string) =>
          FILTER_STATUS_SETS[filter].has(status as SmartOrderStatus);
        // Base entries always -- they're what the list returns to when the
        // filters clear, so they can't be allowed to go stale while they're
        // on.
        writeHistoryCacheForVariables(
          client,
          INBOUND_HISTORY_VARIABLES[filter],
          buildUpdateFn(isIncluded, true),
        );
        // Plus the entries currently on screen, when this tab's effective
        // filters are non-default (a tab whose clamped filters are all-default
        // is reading its base entry, already patched above).
        const effectiveFilters = getEffectiveInboundQueryFilters(
          filter,
          queryFilters,
        );
        if (hasNonDefaultInboundFilters(effectiveFilters)) {
          writeHistoryCacheForVariables(
            client,
            getInboundHistoryFilteredVariables(filter, effectiveFilters),
            buildUpdateFn(isIncluded, false),
          );
        }
      }
    },
    [isInboundHistoryEnabled, updateInboundListQueryCache, queryFilters],
  );

  const getNewOrdersQuery = useLazyQuery<
    OrderInbounds_ListLiveUpdateQuery,
    OrderInbounds_ListLiveUpdateQueryVariables
  >(ORDER_INBOUNDS_LIST_LIVE_UPDATE_QUERY, {
    fetchPolicy: 'network-only',
  });

  const getNewAndUpdatedOrders = useCallback(
    async (orderUUIDs: string[], insertableOrderUUIDs: Set<string>) => {
      try {
        const { data } = await getNewOrdersQuery({
          variables: {
            businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
            orderUUIDs,
          },
        });
        const newOrders = data.smart_order || [];
        if (newOrders.length) {
          // A brand new order can't be matched against a search term or
          // assignee filter locally, so it isn't inserted into a filtered
          // result set -- but it does go into the base entries
          // (canInsertNewRows), so it's already in place the moment the
          // filters are cleared.
          applyLiveCacheUpdate(
            baseListData.client,
            (isIncluded, canInsertNewRows) =>
              buildBackfillCacheUpdate({
                canInsertNewRows,
                fetchedOrders: newOrders,
                insertableOrderUUIDs,
                isIncluded,
              }),
          );
        }
      } catch {
        showErrorToast();
      }
    },
    [
      baseListData.client,
      getNewOrdersQuery,
      applyLiveCacheUpdate,
      showErrorToast,
    ],
  );

  const debouncedGetNewAndUpdatedOrders = useDebouncedCallback(async () => {
    const uuidsToFetch = Array.from(pendingOrderUUIDs.current);
    const insertableUUIDs = new Set(pendingInsertableOrderUUIDs.current);

    if (uuidsToFetch.length > 0) {
      pendingOrderUUIDs.current.clear();
      pendingInsertableOrderUUIDs.current.clear();
      await getNewAndUpdatedOrders(uuidsToFetch, insertableUUIDs);
    }
  }, GET_NEW_ORDERS_DEBOUNCE_DELAY_MS);

  // The subscription needs to know about every order currently cached across
  // whichever query(s) are active, not just the actively-displayed tab --
  // otherwise an update for an order that only belongs in a non-active tab
  // could be missed entirely by the subscription's own where-clause.
  //
  // When filtering, that means the base entries *and* the filtered entries:
  // the base ones are the always-present anchor (so a filter set matching
  // nothing can never shrink the subscription's where-clause to nothing), and
  // the filtered ones can surface orders past page 1 of the base lists, which
  // genuinely aren't cached anywhere else.
  const {
    latestCachedCreatedAt,
    isSubscriptionDataReady,
    existingOrderUUIDs,
    cachedOrdersByUUID,
  } = useMemo(() => {
    if (!isInboundHistoryEnabled) {
      if (!legacyListData.hasResponse) {
        return {
          cachedOrdersByUUID: new Map<string, OrderInboundListFieldsFragment>(),
          existingOrderUUIDs: [],
          isSubscriptionDataReady: false,
          latestCachedCreatedAt: null,
        };
      }
      const orders = legacyListData.data?.smart_order ?? [];
      const map = new Map(orders.map((order) => [order.uuid, order]));
      return {
        cachedOrdersByUUID: map,
        existingOrderUUIDs: Array.from(map.keys()),
        isSubscriptionDataReady: true,
        latestCachedCreatedAt: orders[0]?.created_at ?? null,
      };
    }

    if (
      !openLists.base.hasResponse ||
      !closedLists.base.hasResponse ||
      !allLists.base.hasResponse
    ) {
      return {
        cachedOrdersByUUID: new Map<string, OrderInboundListFieldsFragment>(),
        existingOrderUUIDs: [],
        isSubscriptionDataReady: false,
        latestCachedCreatedAt: null,
      };
    }

    const map = new Map<string, OrderInboundListFieldsFragment>();
    for (const tabData of [
      openLists.base,
      closedLists.base,
      allLists.base,
      openLists.filtered,
      closedLists.filtered,
      allLists.filtered,
    ]) {
      const orders = historyToListShape(tabData.data)?.smart_order ?? [];
      for (const order of orders) {
        map.set(order.uuid, order);
      }
    }
    const mostRecentCreatedAt = Array.from(map.values()).reduce<string | null>(
      (latest, order) =>
        !latest || order.created_at > latest ? order.created_at : latest,
      null,
    );
    return {
      cachedOrdersByUUID: map,
      existingOrderUUIDs: Array.from(map.keys()),
      isSubscriptionDataReady: true,
      latestCachedCreatedAt: mostRecentCreatedAt,
    };
  }, [
    isInboundHistoryEnabled,
    legacyListData.hasResponse,
    legacyListData.data,
    openLists,
    closedLists,
    allLists,
  ]);

  // The subscription's `lastCreatedAt` window is pinned to a ref rather than
  // read straight off the derived value above, which moves around as cache
  // entries are rewritten -- and every change to it tears the subscription down
  // and re-opens it, with a gap in between. The ref is seeded once the lists
  // have responded and only ever advances.
  //
  // An empty inbox has no order to anchor to, so it seeds at "now" -- the
  // subscription still needs to be listening for the first order to arrive.
  // Computing that once, at seed time, is the point: as a per-render fallback
  // it re-armed the window on every render and could skip an order created in
  // between.
  const subscribedFromRef = useRef<string | null>(null);
  if (isSubscriptionDataReady) {
    if (!subscribedFromRef.current) {
      subscribedFromRef.current =
        latestCachedCreatedAt ?? new Date().toISOString();
    } else if (
      latestCachedCreatedAt &&
      latestCachedCreatedAt > subscribedFromRef.current
    ) {
      subscribedFromRef.current = latestCachedCreatedAt;
    }
  }
  const subscribedFrom = subscribedFromRef.current;

  // Whatever the detail pane is showing stays subscribed even if it's in none
  // of the cached lists.
  const subscribedOrderUUIDs = useMemo(() => {
    if (!pinnedOrderUUIDs.length) {
      return existingOrderUUIDs;
    }
    return Array.from(new Set([...existingOrderUUIDs, ...pinnedOrderUUIDs]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingOrderUUIDs, pinnedOrderUUIDs.join(',')]);

  useSubscription<
    OrderInbounds_ListSubscriptionSubscription,
    OrderInbounds_ListSubscriptionSubscriptionVariables
  >(ORDER_INBOUNDS_LIST_SUBSCRIPTION, {
    onData: async (subscriptionData) => {
      try {
        const {
          data: { data },
        } = subscriptionData;
        const updatedOrderFields = data?.employee_smart_orders || [];

        if (
          !Array.isArray(updatedOrderFields) ||
          updatedOrderFields.length === 0
        ) {
          return;
        }

        const subscriptionUpdateSet = new Set(pendingOrderUUIDs.current);
        const insertableUpdateSet = new Set(
          pendingInsertableOrderUUIDs.current,
        );

        const existingUpdates =
          updatedOrderFields.reduce<SmartOrderUpdateMapping>(
            (acc, orderFields) => {
              if (!orderFields.smart_order_uuid) {
                return acc;
              }

              const existingOrder = cachedOrdersByUUID.get(
                orderFields.smart_order_uuid,
              );

              if (existingOrder) {
                const action = getExistingOrderBackfillAction(
                  existingOrder,
                  orderFields,
                  isInboundHistoryEnabled,
                );
                if (action !== 'none') {
                  subscriptionUpdateSet.add(orderFields.smart_order_uuid);
                }
                // Only a status change earns a row in an entry that doesn't
                // hold it yet. A plain data refresh must not be treated as an
                // arrival: the order is already cached somewhere (possibly
                // only in a filtered entry, e.g. an old order surfaced by an
                // oldest-first sort) and prepending it to a newest-first entry
                // would leave it stranded at the top of that list.
                if (action === 'relocate') {
                  insertableUpdateSet.add(orderFields.smart_order_uuid);
                }
                acc[orderFields.smart_order_uuid] = orderFields;
              } else {
                subscriptionUpdateSet.add(orderFields.smart_order_uuid);
                insertableUpdateSet.add(orderFields.smart_order_uuid);
              }
              return acc;
            },
            {},
          );

        if (
          !areSetsEqual(pendingOrderUUIDs.current, subscriptionUpdateSet) ||
          !areSetsEqual(
            pendingInsertableOrderUUIDs.current,
            insertableUpdateSet,
          )
        ) {
          pendingOrderUUIDs.current = subscriptionUpdateSet;
          pendingInsertableOrderUUIDs.current = insertableUpdateSet;
          debouncedGetNewAndUpdatedOrders();
        }

        applyLiveCacheUpdate(
          baseListData.client,
          (isIncluded) => (cachedData) => {
            const updatedOrders = cachedData.smart_order.reduce<
              OrderInboundListFieldsFragment[]
            >((acc, smartOrder) => {
              const updatedFields = existingUpdates[smartOrder.uuid];
              if (!updatedFields) {
                return acc;
              }

              const updatedStatus = updatedFields.status ?? smartOrder.status;
              if (!isIncluded(updatedStatus)) {
                return acc;
              }

              return [
                ...acc,
                {
                  ...smartOrder,
                  assigned_employee: updatedFields.assigned_employee_uuid
                    ? {
                        __typename: 'employees',
                        name: updatedFields.assigned_employee_name!,
                        supplier_employee_overrides:
                          updatedFields.supplier_employee_override_uuid
                            ? [
                                {
                                  __typename: 'supplier_employee_overrides',
                                  name: updatedFields.supplier_employee_override_name,
                                  uuid: updatedFields.supplier_employee_override_uuid,
                                },
                              ]
                            : [],
                        uuid: updatedFields.assigned_employee_uuid,
                      }
                    : null,
                  merge_uuid: updatedFields.merge_uuid,
                  processing_status:
                    updatedFields.processing_status ??
                    smartOrder.processing_status,
                  restaurant: updatedFields.restaurant_uuid
                    ? {
                        __typename: 'businesses',
                        name: updatedFields.restaurant_name!,
                        uuid: updatedFields.restaurant_uuid,
                      }
                    : smartOrder.restaurant,
                  status: updatedStatus,
                },
              ];
            }, []);

            return {
              ...cachedData,
              smart_order: updatedOrders,
            };
          },
        );
      } catch (e) {
        captureException(e);
      }
    },
    skip: !subscribedFrom,
    variables: {
      businessOrganizationUUID: partnerSupplier.businessOrganizationUUID,
      employeeUUID: user.uuid,
      // @ts-ignore skipped if not set
      lastCreatedAt: subscribedFrom,
      orderUUIDs: subscribedOrderUUIDs,
    },
  });

  return {
    ...baseListData,
    fetchNextPage,
    hasLoadFailed,
    isFetchingNextPage,
    isRetryingLoad,
    refetchAll,
    retryActiveTab,
  };
};
